import Cookies from 'js-cookie';
import { ImportedRecipe, ImportJob } from '../models/ImportedRecipe';

const api_path: string = process.env.REACT_APP_BASE_PATH + '/import';

function authHeader(): Record<string, string> {
    const token: string | undefined = Cookies.get('token');
    return { 'Authorization': `Bearer ${token}` };
}

/**
 * The backend has no @ControllerAdvice, so Spring's default error body carries no message - only a
 * status. Callers therefore map the status code to a translated message themselves, which is what
 * this rejects with.
 */
export class ImportError extends Error {
    readonly status: number;

    constructor(status: number) {
        super('Import failed with status ' + status);
        this.status = status;
    }
}

/** Reads a recipe from a web page's structured data. Resolves in a second or two. */
export function importFromUrl(url: string): Promise<ImportedRecipe> {
    return fetch(api_path + '/url', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ url })
    }).then(response => {
        if (!response.ok) {
            throw new ImportError(response.status);
        }
        return response.json();
    });
}

/**
 * Starts a photo import and returns its job id. Photo imports take tens of seconds - too long to
 * hold a request open on a mobile connection - so the work happens on the server and the caller
 * polls {@link getImportJob}.
 */
export function startPhotoImport(files: File[]): Promise<string> {
    const formData: FormData = new FormData();
    files.forEach(file => formData.append('files', file));

    return fetch(api_path + '/photo', {
        method: 'POST',
        // No Content-Type header: the browser has to set the multipart boundary itself.
        headers: authHeader(),
        body: formData
    }).then(response => {
        if (!response.ok) {
            throw new ImportError(response.status);
        }
        return response.json();
    }).then(job => job.jobId);
}

export function getImportJob(jobId: string): Promise<ImportJob> {
    return fetch(api_path + '/job/' + jobId, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', ...authHeader() }
    }).then(response => {
        if (!response.ok) {
            throw new ImportError(response.status);
        }
        return response.json();
    });
}

/**
 * Turns the base64 photo from an import into a File.
 *
 * The recipe editor holds its image as a File and uploads it after the recipe is saved, so
 * converting here means the import needs no changes to that path at all.
 */
export function toImageFile(base64: string, mimeType?: string): Promise<File> {
    const type: string = mimeType ? mimeType : 'image/jpeg';
    return fetch('data:' + type + ';base64,' + base64)
        .then(response => response.blob())
        .then(blob => new File([blob], 'imported.jpg', { type }));
}

/**
 * Shrinks and re-encodes a photo before upload.
 *
 * Three reasons this happens in the browser rather than only on the server: it converts the HEIC an
 * iPhone produces into something the backend can read, it cuts a 5 MB photo to a few hundred
 * kilobytes so the upload finishes on a mobile connection, and it keeps three photos comfortably
 * inside the multipart request limit.
 *
 * The long edge matches what the model the server sends this to will actually look at: an image is
 * fitted into a 2048 by 2048 box on arrival, so anything larger is uploaded over a phone connection
 * and then discarded. Anything smaller costs legibility on small print, which is the one thing this
 * import cannot afford to lose - hence the higher quality than a photo would normally need.
 */
export function downscaleImage(file: File, maxEdge: number = 2048, quality: number = 0.92): Promise<File> {
    return new Promise<File>((resolve) => {
        const objectUrl: string = URL.createObjectURL(file);
        const image = new Image();

        image.onload = () => {
            URL.revokeObjectURL(objectUrl);

            const longEdge: number = Math.max(image.width, image.height);
            const factor: number = longEdge > maxEdge ? maxEdge / longEdge : 1;
            const canvas: HTMLCanvasElement = document.createElement('canvas');
            canvas.width = Math.round(image.width * factor);
            canvas.height = Math.round(image.height * factor);

            const context = canvas.getContext('2d');
            if (!context) {
                resolve(file);
                return;
            }
            context.drawImage(image, 0, 0, canvas.width, canvas.height);

            canvas.toBlob(blob => {
                // Falling back to the original is deliberate: a photo the server might still handle
                // beats refusing the import outright.
                resolve(blob ? new File([blob], 'page.jpg', { type: 'image/jpeg' }) : file);
            }, 'image/jpeg', quality);
        };

        image.onerror = () => {
            URL.revokeObjectURL(objectUrl);
            resolve(file);
        };

        image.src = objectUrl;
    });
}
